/**
 * Main Widget Class for Job Widget SDK
 */

import type {
  WidgetConfig,
  WidgetState,
  Job,
  Application,
  WidgetError,
  ProfileIncompleteData
} from './types';
import { APIClient } from './api';
import { OAuthHandler } from './oauth';
import { EventEmitter } from './events';
import { OAuthCallbackHandler } from './oauth-callback';
import { Logger } from './logger';

export class JobWidget extends EventEmitter {
  private config: WidgetConfig;
  private apiClient: APIClient;
  private oauthHandler: OAuthHandler;
  private state: WidgetState;
  private container: HTMLElement | null = null;
  private initialized: boolean = false;

  constructor(config: WidgetConfig) {
    super();

    // Validate configuration
    this.validateConfig(config);

    // Initialize configuration
    this.config = this.mergeWithDefaults(config);

    // Initialize logger with configuration
    Logger.init({
      debug: this.config.debug || false,
      prefix: 'JobWidget'
    });

    // Initialize API client
    this.apiClient = new APIClient(this.config);

    // Initialize OAuth handler
    this.oauthHandler = new OAuthHandler(this.config);

    // Initialize state
    this.state = {
      initialized: false,
      loading: false,
      error: null,
      user: null,
      jobs: [],
      selectedJob: null,
      applications: [],
      config: this.config
    };

    // Setup container
    this.setupContainer();
  }

  /**
   * Validate widget configuration
   */
  private validateConfig(config: WidgetConfig): void {
    const errors: string[] = [];

    // Required parameters
    if (!config.container) {
      errors.push('container is required');
    }

    if (!config.tenantId) {
      errors.push('tenantId is required for fetching jobs');
    }

    if (!config.apiKey) {
      errors.push('apiKey is required for API authentication');
    }

    // OAuth parameters (required for job applications)
    if (!config.clientId) {
      errors.push('clientId is required for job applications');
    }

    if (!config.redirectUri) {
      errors.push('redirectUri is required for OAuth authentication');
    }

    if (!config.baseUrl) {
      errors.push('baseUrl is required for API and OAuth endpoints');
    }

    if (errors.length > 0) {
      throw new Error(`Missing required parameters:\n${errors.map(e => `- ${e}`).join('\n')}\n\nExample usage:\nnew JobWidget({\n  container: '#widget',\n  tenantId: 'your-tenant-id',\n  apiKey: 'your-api-key',\n  clientId: 'your-oauth-client-id',\n  redirectUri: 'https://yoursite.com/oauth-callback',\n  baseUrl: 'https://api.yourservice.com'\n});`);
    }
  }

  /**
   * Merge configuration with defaults
   */
  private mergeWithDefaults(config: WidgetConfig): WidgetConfig {
    const defaults: Partial<WidgetConfig> = {
      theme: config.theme || 'light',
      type: config.type || 'job-list',
      responsive: config.responsive !== false,
      usePopup: config.usePopup !== false,
      showLogo: config.showLogo !== false,
      showPoweredBy: config.showPoweredBy !== false,
      profileCompletionRequired: config.profileCompletionRequired || 50,
      locale: config.locale || 'en-US',
      debug: config.debug || false,
      sandbox: config.sandbox || false,
      version: config.version || 'v1'
    };

    // Set OAuth defaults if clientId is provided
    if (config.clientId) {
      defaults.redirectUri = config.redirectUri || `${window.location.origin}/job-widget/callback`;
    }

    return {
      ...config,
      ...defaults
    } as WidgetConfig;
  }

  /**
   * Setup container element
   */
  private setupContainer(): void {
    if (typeof this.config.container === 'string') {
      this.container = document.querySelector(this.config.container);
      if (!this.container) {
        throw new Error(`Container element not found: ${this.config.container}`);
      }
    } else {
      this.container = this.config.container;
    }

    // Add widget class
    this.container.classList.add('job-widget');
    this.container.classList.add(`job-widget-${this.config.type}`);

    // Add theme class
    if (this.config.theme) {
      this.container.classList.add(`job-theme-${this.config.theme}`);
    }

    // Add responsive class
    if (this.config.responsive) {
      this.container.classList.add('inzif-responsive');
    }
  }

  /**
   * Initialize widget
   */
  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      this.emit('init', undefined);
      this.updateState({ loading: true });

      await this.loadStyles();

      // Note: User profile endpoint not implemented in backend
      // Widget functions correctly without user profile data

      await this.render();

      this.initialized = true;
      this.updateState({ initialized: true, loading: false });

      this.emit('ready', undefined);

      if (this.config.onReady) {
        this.config.onReady();
      }
    } catch (error) {
      this.handleError(error as WidgetError);
    }
  }

  /**
   * Load widget styles
   */
  private async loadStyles(): Promise<void> {
    // Check if styles already loaded
    if (document.querySelector('#inzif-widget-styles')) {
      return;
    }

    // Add built-in styles for enhanced job widget
    const builtInStyles = document.createElement('style');
    builtInStyles.id = 'inzif-widget-styles';
    builtInStyles.textContent = `
      .inzif-widget {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        color: #374151;
        line-height: 1.5;
      }
      .inzif-job-list {
        padding: 20px;
      }
      .inzif-job-item {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        padding: 24px;
        margin-bottom: 20px;
        transition: box-shadow 0.2s, transform 0.2s;
      }
      .inzif-job-item:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
      }
      .inzif-job-header {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
      }
      .inzif-company-logo {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        object-fit: contain;
        background: #f9fafb;
        padding: 8px;
      }
      .inzif-job-main {
        flex: 1;
      }
      .inzif-job-title {
        margin: 0 0 4px;
        font-size: 20px;
        font-weight: 600;
        color: #111827;
      }
      .inzif-company-name {
        margin: 0 0 12px;
        font-size: 16px;
        color: #6b7280;
      }
      .inzif-job-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-bottom: 16px;
      }
      .inzif-meta-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #6b7280;
      }
      .inzif-meta-item svg {
        color: #9ca3af;
      }
      .inzif-job-description {
        margin: 16px 0;
        padding: 16px;
        background: #f9fafb;
        border-radius: 8px;
      }
      .inzif-description-text {
        margin: 0;
        font-size: 14px;
        color: #4b5563;
        white-space: pre-wrap;
      }
      .inzif-expand-btn {
        margin-top: 8px;
        padding: 6px 12px;
        background: transparent;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.2s;
      }
      .inzif-expand-btn:hover {
        background: #ffffff;
        border-color: #9ca3af;
        color: #374151;
      }
      .inzif-salary-range {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin: 12px 0;
        padding: 8px 16px;
        background: #ecfdf5;
        color: #065f46;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
      }
      .inzif-salary-range svg {
        color: #059669;
      }
      .inzif-job-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #e5e7eb;
      }
      .inzif-apply-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 10px 24px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        min-width: 160px;
      }
      .inzif-apply-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(102, 126, 234, 0.4);
      }
      .inzif-apply-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .inzif-apply-button.already-applied {
        background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
        color: #4a5568;
        cursor: default;
        position: relative;
      }
      .inzif-apply-button.already-applied::before {
        content: '✓';
        position: absolute;
        left: 12px;
        font-size: 18px;
        color: #48bb78;
        font-weight: bold;
      }
      .inzif-deadline {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: #dc2626;
      }
      .inzif-deadline svg {
        color: #ef4444;
      }

      /* Widget Footer Styles */
      .inzif-widget-footer {
        margin-top: 32px;
        padding-top: 24px;
        border-top: 1px solid #e5e7eb;
        font-size: 14px;
        color: #6b7280;
        animation: fadeIn 0.5s ease-out;
      }
      .inzif-footer-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      .inzif-footer-branding {
        font-weight: 500;
      }
      .inzif-powered-by a {
        color: #6366f1;
        text-decoration: none;
        font-weight: 600;
        transition: color 0.2s;
      }
      .inzif-powered-by a:hover {
        color: #4f46e5;
        text-decoration: underline;
      }
      .inzif-footer-links {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .inzif-footer-links a {
        color: #6b7280;
        text-decoration: none;
        transition: color 0.2s;
      }
      .inzif-footer-links a:hover {
        color: #374151;
        text-decoration: underline;
      }
      .inzif-footer-separator {
        color: #d1d5db;
      }
      .inzif-footer-copyright {
        text-align: center;
        font-size: 13px;
        color: #9ca3af;
        padding-top: 12px;
      }

      /* Loading Skeleton Styles */
      .inzif-skeleton {
        animation: skeleton-loading 1.2s linear infinite;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        border-radius: 8px;
      }
      .inzif-skeleton-job {
        height: 180px;
        margin-bottom: 20px;
      }
      @keyframes skeleton-loading {
        0% { background-position: -200px 0; }
        100% { background-position: calc(200px + 100%) 0; }
      }

      /* Smooth Animations */
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .inzif-job-item {
        animation: slideUp 0.4s ease-out;
        animation-fill-mode: both;
      }
      .inzif-job-item:nth-child(1) { animation-delay: 0.1s; }
      .inzif-job-item:nth-child(2) { animation-delay: 0.2s; }
      .inzif-job-item:nth-child(3) { animation-delay: 0.3s; }
      .inzif-job-item:nth-child(4) { animation-delay: 0.4s; }
      .inzif-job-item:nth-child(5) { animation-delay: 0.5s; }

      /* Empty State */
      .inzif-empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #6b7280;
      }
      .inzif-empty-icon {
        width: 64px;
        height: 64px;
        margin: 0 auto 16px;
        opacity: 0.5;
      }
      .inzif-empty-title {
        font-size: 18px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 8px;
      }
      .inzif-empty-message {
        font-size: 14px;
        color: #9ca3af;
      }

      /* Error State */
      .inzif-error-state {
        text-align: center;
        padding: 40px 20px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 12px;
        margin: 20px;
      }
      .inzif-error-icon {
        width: 48px;
        height: 48px;
        margin: 0 auto 16px;
        color: #ef4444;
      }
      .inzif-error-title {
        font-size: 16px;
        font-weight: 600;
        color: #991b1b;
        margin-bottom: 8px;
      }
      .inzif-error-message {
        font-size: 14px;
        color: #b91c1c;
        margin-bottom: 16px;
      }
      .inzif-retry-button {
        padding: 8px 20px;
        background: #ef4444;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }
      .inzif-retry-button:hover {
        background: #dc2626;
      }

      /* Responsive Design */
      @media (max-width: 640px) {
        .inzif-job-header {
          flex-direction: column;
        }
        .inzif-company-logo {
          width: 48px;
          height: 48px;
        }
        .inzif-job-meta {
          flex-direction: column;
          gap: 8px;
        }
        .inzif-job-actions {
          flex-direction: column;
          gap: 12px;
        }
        .inzif-apply-button {
          width: 100%;
        }
        .inzif-footer-content {
          flex-direction: column;
          gap: 12px;
          text-align: center;
        }
        .inzif-footer-links {
          order: 2;
        }
        .inzif-footer-branding {
          order: 1;
        }
      }
    `;
    document.head.appendChild(builtInStyles);

    // Load external styles if specified
    if (this.config.baseUrl) {
      const styleLink = document.createElement('link');
      styleLink.rel = 'stylesheet';
      styleLink.href = `${this.config.baseUrl}/assets/styles.css`;
      document.head.appendChild(styleLink);
    }

    // Add custom styles if provided
    if (this.config.customStyles) {
      const customStyle = document.createElement('style');
      customStyle.id = 'inzif-widget-custom-styles';
      customStyle.textContent = this.config.customStyles;
      document.head.appendChild(customStyle);
    }

    // Add font family if provided
    if (this.config.fontFamily) {
      const fontStyle = document.createElement('style');
      fontStyle.textContent = `.inzif-widget { font-family: ${this.config.fontFamily}; }`;
      document.head.appendChild(fontStyle);
    }

    // Add primary color if provided
    if (this.config.primaryColor) {
      const colorStyle = document.createElement('style');
      colorStyle.textContent = `:root { --inzif-primary: ${this.config.primaryColor}; --inzif-primary-dark: ${this.darkenColor(this.config.primaryColor, 20)}; }`;
      document.head.appendChild(colorStyle);
    }
  }

  /**
   * Darken a hex color
   */
  private darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  /**
   * Load user profile
   */
  private async loadUserProfile(): Promise<void> {
    try {
      const user = await this.apiClient.getUserProfile();
      this.updateState({ user });
    } catch (error) {
      // User not authenticated, continue without profile
      Logger.debugSafe('User not authenticated', error);
    }
  }

  /**
   * Disconnect user (clear authentication)
   */
  public async disconnect(): Promise<void> {
    // Clear stored tokens and state
    this.apiClient.clearStorage();

    // Reset widget state
    this.updateState({
      user: null,
      applications: []
    });

    // Emit disconnect event
    this.emit('auth:disconnect', undefined);

    // Call callback if provided
    if (this.config.onDisconnect) {
      this.config.onDisconnect();
    }

    // Re-render the widget to show logged out state
    await this.render();

    // Don't show the message - the UI already reflects the state change
  }

  /**
   * Render widget based on type
   */
  private async render(): Promise<void> {
    if (!this.container) return;

    // Always render job list (only widget type now)
    await this.renderJobList();
  }

  /**
   * Render job list widget
   */
  private async renderJobList(): Promise<void> {
    try {
      // Show loading skeleton while fetching
      this.showLoadingSkeleton();

      const response = await this.apiClient.getJobs();

      // Handle both 'jobs' and 'items' field names for compatibility
      const jobsList = (response as any).jobs || (response as any).items || [];

      if (!Array.isArray(jobsList)) {
        Logger.error('Jobs list is not an array');
        throw new Error('Invalid response format - expected jobs array');
      }

      this.updateState({ jobs: jobsList });

      // Clear container before adding new content to prevent duplicates
      this.container!.innerHTML = '';

      const listContainer = document.createElement('div');
      listContainer.className = 'inzif-job-list';

      // Show empty state if no jobs
      if (jobsList.length === 0) {
        const emptyState = this.createEmptyState();
        this.container!.appendChild(emptyState);

        // Add widget footer
        const footer = this.createWidgetFooter();
        this.container!.appendChild(footer);
        return;
      }

      jobsList.forEach((job: Job) => {
        const jobItem = this.createJobItem(job);
        listContainer.appendChild(jobItem);
      });

      this.container!.appendChild(listContainer);

      // Add widget footer
      const footer = this.createWidgetFooter();
      this.container!.appendChild(footer);
    } catch (error) {
      this.showErrorState(error as WidgetError);
    }
  }

  /**
   * Create job item element
   */
  private createJobItem(job: Job): HTMLElement {
    const item = document.createElement('div');
    item.className = 'inzif-job-item';

    const formatEmploymentType = (type: string) => {
      const types: Record<string, string> = {
        'full_time': 'Full Time',
        'part_time': 'Part Time',
        'contract': 'Contract',
        'internship': 'Internship',
        'temporary': 'Temporary'
      };
      return types[type] || type;
    };

    const formatExperienceLevel = (level: string) => {
      const levels: Record<string, string> = {
        'fresher': 'Fresher',
        'entry': 'Entry Level',
        'mid': 'Mid Level',
        'senior': 'Senior',
        'lead': 'Lead',
        'manager': 'Manager'
      };
      return levels[level] || level;
    };

    const truncateDescription = (desc: string, maxLength: number = 200) => {
      if (!desc || desc.length <= maxLength) return desc;
      return desc.substring(0, maxLength) + '...';
    };

    item.innerHTML = `
      <div class="inzif-job-header">
        ${job.company_logo ? `<img src="${job.company_logo}" alt="${job.company_name || 'Company'}" class="inzif-company-logo">` : ''}
        <div class="inzif-job-main">
          <h3 class="inzif-job-title">${job.title}</h3>
          ${job.company_name ? `<p class="inzif-company-name">${job.company_name}</p>` : ''}
          <div class="inzif-job-meta">
            ${job.department ? `<span class="inzif-meta-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 7h-9m0 10h9m-15-6a2 2 0 1 0 4 0 2 2 0 1 0-4 0m0-8a2 2 0 1 0 4 0 2 2 0 1 0-4 0m0 16a2 2 0 1 0 4 0 2 2 0 1 0-4 0"/></svg>${job.department}</span>` : ''}
            ${job.location ? `<span class="inzif-meta-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>${job.location}${job.is_remote ? ' (Remote Available)' : ''}</span>` : ''}
            ${job.employment_type ? `<span class="inzif-meta-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>${formatEmploymentType(job.employment_type)}</span>` : ''}
            ${job.experience_level ? `<span class="inzif-meta-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M2 12h20"/></svg>${formatExperienceLevel(job.experience_level)}</span>` : ''}
          </div>
        </div>
      </div>
      ${job.description ? `
        <div class="inzif-job-description" data-expanded="false">
          <p class="inzif-description-text">${truncateDescription(job.description)}</p>
          ${job.description.length > 200 ? `
            <button class="inzif-expand-btn" data-job-id="${job.id}">
              Show more
            </button>
          ` : ''}
        </div>
      ` : ''}
      ${job.salary_min || job.salary_max ? `
        <div class="inzif-salary-range">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          ${job.salary_min && job.salary_max ? `${job.salary_currency || '$'}${job.salary_min.toLocaleString()} - ${job.salary_currency || '$'}${job.salary_max.toLocaleString()}` :
            job.salary_min ? `From ${job.salary_currency || '$'}${job.salary_min.toLocaleString()}` :
            `Up to ${job.salary_currency || '$'}${job.salary_max?.toLocaleString()}`}
        </div>
      ` : ''}
      <div class="inzif-job-actions">
        <button class="inzif-apply-button ${this.hasAppliedToJob(job.id) ? 'already-applied' : ''}"
                data-job-id="${job.id}"
                ${this.hasAppliedToJob(job.id) ? 'disabled' : ''}>
          ${this.getButtonText(job.id)}
        </button>
        ${job.application_deadline ? `
          <span class="inzif-deadline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            Apply by ${new Date(job.application_deadline).toLocaleDateString()}
          </span>
        ` : ''}
      </div>
    `;

    const button = item.querySelector('.inzif-apply-button') as HTMLButtonElement;
    button.onclick = () => this.handleApply(job);

    const expandBtn = item.querySelector('.inzif-expand-btn') as HTMLButtonElement;
    if (expandBtn && job.description) {
      expandBtn.onclick = () => {
        const descContainer = item.querySelector('.inzif-job-description') as HTMLElement;
        const descText = item.querySelector('.inzif-description-text') as HTMLElement;
        const isExpanded = descContainer.getAttribute('data-expanded') === 'true';

        if (isExpanded) {
          descText.textContent = truncateDescription(job.description!);
          expandBtn.textContent = 'Show more';
          descContainer.setAttribute('data-expanded', 'false');
        } else {
          descText.textContent = job.description!;
          expandBtn.textContent = 'Show less';
          descContainer.setAttribute('data-expanded', 'true');
        }
      };
    }

    return item;
  }


  /**
   * Show loading skeleton
   */
  private showLoadingSkeleton(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="inzif-job-list">
        <div class="inzif-skeleton inzif-skeleton-job"></div>
        <div class="inzif-skeleton inzif-skeleton-job"></div>
        <div class="inzif-skeleton inzif-skeleton-job"></div>
      </div>
    `;
  }

  /**
   * Create empty state
   */
  private createEmptyState(): HTMLElement {
    const emptyState = document.createElement('div');
    emptyState.className = 'inzif-empty-state';

    emptyState.innerHTML = `
      <svg class="inzif-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
        <path d="m3.3 7 8.7 5 8.7-5"/>
        <path d="M12 22V12"/>
      </svg>
      <h3 class="inzif-empty-title">No Positions Available</h3>
      <p class="inzif-empty-message">Check back soon for new opportunities!</p>
    `;

    return emptyState;
  }

  /**
   * Show error state
   */
  private showErrorState(error: WidgetError): void {
    if (!this.container) return;

    const errorState = document.createElement('div');
    errorState.className = 'inzif-error-state';

    const errorMessage = error.message || 'Something went wrong while loading jobs';

    errorState.innerHTML = `
      <svg class="inzif-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="m15 9-6 6"/>
        <path d="m9 9 6 6"/>
      </svg>
      <h3 class="inzif-error-title">Unable to Load Jobs</h3>
      <p class="inzif-error-message">${errorMessage}</p>
      <button class="inzif-retry-button" onclick="window.location.reload()">
        Try Again
      </button>
    `;

    this.container.innerHTML = '';
    this.container.appendChild(errorState);

    // Add widget footer even on error
    const footer = this.createWidgetFooter();
    this.container.appendChild(footer);

    this.handleError(error);
  }

  /**
   * Create widget footer with branding and links
   */
  private createWidgetFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'inzif-widget-footer';

    const currentYear = new Date().getFullYear();
    const termsUrl = 'https://inzif.com/terms';
    const privacyUrl = 'https://inzif.com/privacy';
    const companyUrl = 'https://inzif.com';
    const isAuthenticated = this.apiClient.isAuthenticated();

    footer.innerHTML = `
      <div class="inzif-footer-content">
        <div class="inzif-footer-branding">
          <span class="inzif-powered-by">
            Powered by <a href="${companyUrl}" target="_blank" rel="noopener noreferrer">Inzif</a>
          </span>
        </div>
        <div class="inzif-footer-links">
          <a href="${termsUrl}" target="_blank" rel="noopener noreferrer">Terms</a>
          <span class="inzif-footer-separator">•</span>
          <a href="${privacyUrl}" target="_blank" rel="noopener noreferrer">Privacy</a>
          ${isAuthenticated ? `
            <span class="inzif-footer-separator">•</span>
            <a href="#" class="inzif-disconnect-footer-link">Disconnect</a>
          ` : ''}
        </div>
      </div>
      <div class="inzif-footer-copyright">
        © ${currentYear} Inzif. All rights reserved.
      </div>
    `;

    // Add disconnect handler if authenticated
    if (isAuthenticated) {
      const disconnectLink = footer.querySelector('.inzif-disconnect-footer-link');
      if (disconnectLink) {
        disconnectLink.addEventListener('click', async (e) => {
          e.preventDefault();
          await this.disconnect();
        });
      }
    }

    return footer;
  }

  /**
   * Apply to job (public method for external use)
   */
  async applyToJob(jobId: string, tenantId: string, data?: {
    cover_letter?: string;
    resume_id?: string;
    answers?: Record<string, any>;
  }): Promise<Application | ProfileIncompleteData> {
    // Check if already applied
    if (this.hasAppliedToJob(jobId)) {
      const error: WidgetError = {
        code: 'ALREADY_APPLIED',
        message: 'You have already applied to this job'
      };
      this.handleError(error);
      throw error;
    }

    const result = await this.apiClient.applyToJob(jobId, tenantId, data);

    if ('profile_completeness' in result) {
      // Store pending application for after profile completion
      sessionStorage.setItem('job_widget_pending_application', JSON.stringify({
        jobId,
        tenantId,
        data
      }));
      this.showProfileCompletionModal(result as ProfileIncompleteData);
      return result;
    }

    const application = result as Application;
    this.markJobAsApplied(jobId);
    this.emit('apply:success', application);

    if (this.config.onSuccess) {
      this.config.onSuccess(application);
    }

    this.storeApplication(application);
    this.showSuccessMessage();

    return application;
  }

  /**
   * Handle apply button click
   */
  private async handleApply(job?: Job): Promise<void> {
    try {
      if (job) {
        this.updateState({ selectedJob: job });
      }

      const targetJob = job || this.state.selectedJob;
      if (!targetJob) {
        throw new Error('No job selected');
      }

      if (this.hasAppliedToJob(targetJob.id)) {
        this.showInfoMessage('You have already applied to this job');
        return;
      }

      this.emit('apply:start', targetJob);

      if (this.config.onApply) {
        this.config.onApply(targetJob);
      }

      let retryCount = 0;
      const maxRetries = 1;

      while (retryCount <= maxRetries) {
        try {
          if (!this.apiClient.isAuthenticated()) {
            await this.authenticate();
          }

          const tenantId = this.config.tenantId || '';
          const result = await this.apiClient.applyToJob(targetJob.id, tenantId);

          if ('status' in result && result.status === 'already_applied') {
            this.markJobAsApplied(targetJob.id);
            this.showInfoMessage('You have already applied to this job');
            return;
          }

          if ('profile_completeness' in result) {
            this.showProfileCompletionModal(result as ProfileIncompleteData);
            return;
          }

          const application = result as Application;
          this.markJobAsApplied(targetJob.id);
          this.emit('apply:success', application);

          if (this.config.onSuccess) {
            this.config.onSuccess(application);
          }

          this.storeApplication(application);
          this.showSuccessMessage();
          return;

        } catch (error) {
          const widgetError = error as WidgetError;

          if (widgetError.code === 'AUTH_EXPIRED' && retryCount < maxRetries) {
            retryCount++;
            await this.authenticate();
            continue;
          }

          if (widgetError.code === 'AUTH_REQUIRED' && retryCount < maxRetries) {
            retryCount++;
            await this.authenticate();
            continue;
          }

          throw widgetError;
        }
      }

    } catch (error) {
      const widgetError = error as WidgetError;
      this.emit('apply:failed', widgetError);
      this.handleError(widgetError);
    }
  }

  /**
   * Authenticate user (public method for React wrapper)
   */
  async authenticate(): Promise<void> {
    try {
      this.emit('auth:required', undefined);

      if (this.config.onAuthRequired) {
        this.config.onAuthRequired();
      }

      const token = await this.oauthHandler.authenticate();

      this.emit('auth:success', token);

      // Note: Skip user profile load - endpoint not available

      // Re-render widget to update UI
      await this.render();

      // Check for pending application
      await this.resumePendingApplication();

    } catch (error) {
      const authError = error as WidgetError;
      this.emit('auth:failed', authError);
      throw authError;
    }
  }

  /**
   * Handle profile incomplete
   */
  private handleProfileIncomplete(data: ProfileIncompleteData): void {
    this.emit('profile:incomplete', data);

    if (this.config.onProfileIncomplete) {
      this.config.onProfileIncomplete(data);
    }

    // Show profile completion message
    this.showProfileIncompleteMessage(data);
  }

  /**
   * Show success message
   */
  private showSuccessMessage(): void {
    const message = document.createElement('div');
    message.className = 'inzif-message inzif-success';
    message.textContent = this.getTranslation('application_success');

    document.body.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 5000);
  }

  /**
   * Show info message
   */
  private showInfoMessage(text: string): void {
    const message = document.createElement('div');
    message.className = 'inzif-message inzif-info';
    message.textContent = text;

    document.body.appendChild(message);

    setTimeout(() => {
      message.remove();
    }, 5000);
  }

  /**
   * Show profile incomplete message
   */
  private showProfileIncompleteMessage(data: ProfileIncompleteData): void {
    const message = document.createElement('div');
    message.className = 'inzif-message inzif-warning';
    message.innerHTML = `
      <p>${data.message}</p>
      <a href="${data.profile_url}" target="_blank" class="inzif-profile-link">
        ${this.getTranslation('complete_profile')}
      </a>
    `;

    this.container!.appendChild(message);
  }

  /**
   * Show profile completion modal
   */
  private showProfileCompletionModal(data: ProfileIncompleteData): void {
    // Remove any existing modal
    const existingModal = document.getElementById('inzif-profile-modal');
    if (existingModal) {
      existingModal.remove();
    }

    const progressPercent = Math.round(data.profile_completeness || 0);
    const requiredPercent = Math.round(data.minimum_required || 50);

    const missingSections = (data.missing_sections || []).map((section: string) =>
      section.charAt(0).toUpperCase() + section.slice(1)
    ).join(', ') || 'Profile details';

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'inzif-profile-modal';
    modal.className = 'inzif-profile-modal';
    modal.innerHTML = `
      <div class="inzif-modal-overlay"></div>
      <div class="inzif-modal-content">
        <div class="inzif-modal-header">
          <h3>Complete Your Profile to Apply</h3>
          <button class="inzif-modal-close" aria-label="Close">×</button>
        </div>
        <div class="inzif-modal-body">
          <p class="inzif-modal-message">
            Your profile is <strong>${progressPercent}% complete</strong>.
            This position requires a minimum of <strong>${requiredPercent}%</strong> profile completion.
          </p>

          <div class="inzif-progress-container">
            <div class="inzif-progress-bar">
              <div class="inzif-progress-fill" style="width: ${progressPercent}%"></div>
              <div class="inzif-progress-required" style="left: ${requiredPercent}%"></div>
            </div>
            <div class="inzif-progress-labels">
              <span class="current">${progressPercent}%</span>
              <span class="required">Required: ${requiredPercent}%</span>
            </div>
          </div>

          <div class="inzif-missing-sections">
            <p><strong>Complete these sections:</strong></p>
            <p class="sections">${missingSections}</p>
          </div>

          <div class="inzif-modal-guidance">
            <p>✨ It only takes a few minutes to complete your profile and apply for this position!</p>
          </div>
        </div>
        <div class="inzif-modal-footer">
          <button class="inzif-btn-secondary" id="inzif-cancel-btn">
            Maybe Later
          </button>
          <button class="inzif-btn-primary" id="inzif-complete-profile-btn">
            Complete Profile →
          </button>
        </div>
      </div>
    `;

    // Add styles
    this.addModalStyles();

    // Add to body
    document.body.appendChild(modal);

    // Add event listeners
    const closeBtn = modal.querySelector('.inzif-modal-close') as HTMLButtonElement;
    const cancelBtn = modal.querySelector('#inzif-cancel-btn') as HTMLButtonElement;
    const completeBtn = modal.querySelector('#inzif-complete-profile-btn') as HTMLButtonElement;
    const overlay = modal.querySelector('.inzif-modal-overlay') as HTMLDivElement;

    const closeModal = () => {
      modal.classList.add('closing');
      setTimeout(() => modal.remove(), 300);
    };

    closeBtn.onclick = closeModal;
    cancelBtn.onclick = closeModal;
    overlay.onclick = closeModal;

    completeBtn.onclick = (e: MouseEvent) => {
      e.preventDefault();

      const profileWindow = window.open(
        data.profile_url || 'https://inzif.com/profile',
        '_blank',
        'noopener,noreferrer'
      );

      if (!profileWindow || profileWindow.closed) {
        const profileLink = document.createElement('a');
        profileLink.href = data.profile_url || 'https://inzif.com/profile';
        profileLink.target = '_blank';
        profileLink.rel = 'noopener noreferrer';
        profileLink.className = 'inzif-btn-primary';
        profileLink.textContent = 'Open Profile in New Tab →';
        profileLink.style.textDecoration = 'none';
        profileLink.style.display = 'inline-block';

        completeBtn.replaceWith(profileLink);
      } else {
        completeBtn.textContent = 'Profile Opened ✓';
        completeBtn.style.background = '#48bb78';
        completeBtn.disabled = true;

        setTimeout(() => {
          closeModal();
          this.showInfoMessage('Complete your profile in the new tab, then click Apply again!');
        }, 1500);
      }
    };

    // Show modal with animation
    setTimeout(() => modal.classList.add('show'), 10);
  }

  /**
   * Add modal styles
   */
  private addModalStyles(): void {
    if (document.getElementById('inzif-modal-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'inzif-modal-styles';
    styles.textContent = `
      #inzif-profile-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      .inzif-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        animation: fadeIn 0.3s ease;
      }
      .inzif-modal-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        max-width: 480px;
        width: 90%;
        max-height: 90vh;
        overflow: auto;
        animation: slideIn 0.3s ease;
      }
      .inzif-modal-header {
        padding: 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .inzif-modal-header h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #111827;
      }
      .inzif-modal-close {
        background: none;
        border: none;
        font-size: 24px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        transition: all 0.2s;
      }
      .inzif-modal-close:hover {
        background: #f3f4f6;
        color: #111827;
      }
      .inzif-modal-body {
        padding: 24px;
      }
      .inzif-progress-container {
        position: relative;
        height: 24px;
        background: #f3f4f6;
        border-radius: 12px;
        overflow: hidden;
        margin-bottom: 20px;
      }
      .inzif-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: linear-gradient(90deg, #3b82f6, #6366f1);
        border-radius: 12px;
        transition: width 0.3s ease;
      }
      .inzif-progress-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
        font-weight: 600;
        color: #374151;
      }
      .inzif-modal-message {
        font-size: 14px;
        color: #6b7280;
        margin: 0 0 20px;
        line-height: 1.5;
      }
      .inzif-missing-fields {
        background: #fef3c7;
        border: 1px solid #fcd34d;
        border-radius: 8px;
        padding: 16px;
      }
      .inzif-missing-fields h4 {
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: 600;
        color: #92400e;
      }
      .inzif-missing-fields ul {
        margin: 0;
        padding-left: 20px;
      }
      .inzif-missing-fields li {
        font-size: 14px;
        color: #92400e;
        margin: 4px 0;
      }
      .inzif-modal-footer {
        padding: 24px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }
      .inzif-btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
        outline: none;
      }
      .inzif-btn-primary {
        background: #3b82f6;
        color: white;
      }
      .inzif-btn-primary:hover {
        background: #2563eb;
      }
      .inzif-btn-secondary {
        background: #f3f4f6;
        color: #374151;
      }
      .inzif-btn-secondary:hover {
        background: #e5e7eb;
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translate(-50%, -48%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }
    `;
    document.head.appendChild(styles);
  }

  /**
   * Check if user has already applied to a job
   */
  private hasAppliedToJob(jobId: string): boolean {
    const appliedJobs = this.getAppliedJobs();
    return appliedJobs.includes(jobId);
  }

  /**
   * Get button text based on auth and applied status
   */
  private getButtonText(jobId: string): string {
    if (this.hasAppliedToJob(jobId)) {
      return 'Already Applied';
    }
    if (this.apiClient.isAuthenticated()) {
      return 'Apply Now';
    }
    return 'Apply with Inzif';
  }

  /**
   * Mark job as applied
   */
  private markJobAsApplied(jobId: string): void {
    const appliedJobs = this.getAppliedJobs();
    if (!appliedJobs.includes(jobId)) {
      appliedJobs.push(jobId);
      sessionStorage.setItem('job_widget_applied_jobs', JSON.stringify(appliedJobs));
    }
  }

  /**
   * Get list of applied jobs
   */
  private getAppliedJobs(): string[] {
    const stored = sessionStorage.getItem('job_widget_applied_jobs');
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Store application
   */
  private storeApplication(application: Application): void {
    const applications = this.getStoredApplications();
    applications.push(application);
    sessionStorage.setItem('job_widget_applications', JSON.stringify(applications));
  }

  /**
   * Get stored applications
   */
  private getStoredApplications(): Application[] {
    const stored = sessionStorage.getItem('job_widget_applications');
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Update widget state
   */
  private updateState(updates: Partial<WidgetState>): void {
    this.state = { ...this.state, ...updates };
  }

  /**
   * Handle errors
   */
  private handleError(error: WidgetError): void {
    this.updateState({ error, loading: false });
    this.emit('error', error);

    if (this.config.onError) {
      this.config.onError(error);
    }

    Logger.error('Job Widget Error', error);
  }

  /**
   * Get translation
   */
  private getTranslation(key: string): string {
    const translations: Record<string, string> = {
      apply: 'Apply',
      apply_now: 'Apply Now',
      application_success: 'Application submitted successfully!',
      complete_profile: 'Complete Your Profile',
      profile_incomplete: 'Profile Incomplete',
      missing_fields: 'Missing Fields',
      cancel: 'Cancel',
      ...this.config.translations
    };

    return translations[key] || key;
  }

  /**
   * Get widget state
   */
  getState(): WidgetState {
    return { ...this.state };
  }

  /**
   * Create apply button (legacy method)
   */
  createButton(
    container: HTMLElement | string,
    jobId: string,
    options?: {
      text?: string;
      className?: string;
      onSuccess?: (result: Application) => void;
      onError?: (error: WidgetError) => void;
    }
  ): HTMLButtonElement {
    const containerEl = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement
      : container;

    if (!containerEl) {
      throw new Error('Button container element not found');
    }

    const button = document.createElement('button');
    button.className = options?.className || 'inzif-apply-button';
    button.textContent = options?.text || this.getTranslation('apply_now');

    button.onclick = async () => {
      try {
        // Use tenantId from config for single button creation
        const tenantId = this.config.tenantId || '';
        const result = await this.applyToJob(jobId, tenantId);
        if (options?.onSuccess) {
          options.onSuccess(result as Application);
        }
      } catch (error) {
        if (options?.onError) {
          options.onError(error as WidgetError);
        }
      }
    };

    containerEl.appendChild(button);
    return button;
  }

  /**
   * Get current access token (legacy method)
   */
  getToken(): string | null {
    const tokenStr = sessionStorage.getItem('job_widget_token');
    if (!tokenStr) {
      // Check legacy token storage
      return sessionStorage.getItem('job_widget_access_token');
    }

    try {
      const token = JSON.parse(tokenStr);
      if (token.expires_at > Date.now()) {
        return token.access_token;
      }
    } catch {
      // Try legacy format
      return sessionStorage.getItem('job_widget_access_token');
    }

    return null;
  }

  /**
   * Static method to handle OAuth callback page
   */
  static handleOAuthCallback(): void {
    OAuthCallbackHandler.renderCallbackPage();
  }

  /**
   * Static method to handle OAuth callback (legacy compatibility)
   */
  static handleCallback(): void {
    // Get URL parameters
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      // Send error to parent window if in popup
      if (window.opener) {
        window.opener.postMessage({
          type: 'JOB_WIDGET_OAUTH_CALLBACK',
          error: error
        }, window.location.origin);
      }
      return;
    }

    if (code && state) {
      // Verify state
      const storedState = sessionStorage.getItem('job_widget_state');
      if (state === storedState) {
        // Send code to parent window if in popup
        if (window.opener) {
          window.opener.postMessage({
            type: 'JOB_WIDGET_OAUTH_CALLBACK',
            code: code,
            state: state
          }, window.location.origin);
          window.close();
        } else {
          // Store code for later exchange
          sessionStorage.setItem('job_widget_oauth_code', code);

          // Redirect back to original page
          const returnUrl = sessionStorage.getItem('job_widget_return_url');
          if (returnUrl) {
            window.location.href = returnUrl;
          }
        }
      }
    }
  }

  /**
   * Generate state for OAuth (legacy compatibility)
   */
  generateState(): string {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Create multiple apply buttons
   */
  createButtons(
    buttons: Array<{
      container: HTMLElement | string;
      jobId: string;
      tenantId?: string;
      text?: string;
      className?: string;
      onSuccess?: (result: Application) => void;
      onError?: (error: WidgetError) => void;
    }>
  ): HTMLButtonElement[] {
    const createdButtons: HTMLButtonElement[] = [];

    buttons.forEach(config => {
      const containerEl = typeof config.container === 'string'
        ? document.querySelector(config.container) as HTMLElement
        : config.container;

      if (!containerEl) {
        Logger.warn(`Button container not found: ${config.container}`);
        return;
      }

      const button = document.createElement('button');
      button.className = config.className || 'inzif-apply-button';
      button.textContent = config.text || this.getTranslation('apply_now');

      button.onclick = async () => {
        try {
          const tenantId = config.tenantId || this.config.tenantId || '';
          const result = await this.applyToJob(config.jobId, tenantId);
          if (config.onSuccess) {
            config.onSuccess(result as Application);
          }
        } catch (error) {
          if (config.onError) {
            config.onError(error as WidgetError);
          }
        }
      };

      containerEl.appendChild(button);
      createdButtons.push(button);
    });

    return createdButtons;
  }

  /**
   * Resume pending application after profile completion
   */
  async resumePendingApplication(): Promise<void> {
    const pendingStr = sessionStorage.getItem('job_widget_pending_application');
    if (!pendingStr) return;

    try {
      const pending = JSON.parse(pendingStr);
      sessionStorage.removeItem('job_widget_pending_application');
      await this.applyToJob(pending.jobId, pending.tenantId, pending.data);
    } catch (error) {
      Logger.error('Failed to resume pending application', error);
    }
  }


  /**
   * Destroy widget
   */
  destroy(): void {
    this.removeAllListeners();

    this.oauthHandler.destroy();

    if (this.container) {
      this.container.innerHTML = '';
      this.container.classList.remove('inzif-widget');
    }

    this.initialized = false;
  }
}